/**
 * System Error Codes Allow-List
 *
 * Implements US-016 AC-1/AC-2/AC-3: Centralised gate for the "Report a problem"
 * affordance. Only error codes in this set represent genuine system errors that
 * warrant showing the Report Issue dialog. User-initiated or business-rule errors
 * are deliberately excluded so the button never appears for those.
 *
 * Source: api.ts interceptor (mapStatusToCode) emits the code strings consumed here.
 * UNAUTHORIZED is included because any UNAUTHORIZED that reaches this gate means
 * silent token refresh already FAILED (a successful refresh never surfaces an error).
 */

/**
 * Set of error codes that count as genuine system errors (AC-1).
 * Each code is documented with its source and the reason it is included.
 *
 * Excluded codes (must NOT trigger the Report Issue button — AC-2):
 *   'VALIDATION_ERROR'           — student submitted invalid input (400 from mapStatusToCode)
 *   'INSUFFICIENT_COINS'         — business limit: not enough OtterCoins
 *   'DEMO_ATTEMPT_LIMIT'         — demo-gate limit: too many attempts per question
 *   'DEMO_QUESTION_LIMIT'        — demo-gate limit: too many questions per session
 *   'DEMO_SESSION_LIMIT'         — demo-gate limit: all demo sessions used
 *   'COOLDOWN_ACTIVE'            — business rule: must wait before retrying
 *   'TURN_ALREADY_APPLIED'       — BUG-20260731-163953: benign, self-healing
 *                                  concurrent-turn rejection (409); the client
 *                                  refetches, this is never a genuine error
 *   'SESSION_ALREADY_ACTIVE'     — session-access: student already has an active session
 *   'SESSION_ALREADY_COMPLETED'  — session-access: session is already finished
 *   'NO_CONTENT_AVAILABLE'       — content service: no passage generated yet (transient)
 *   'CONFLICT'                   — general 409 conflict (409 from mapStatusToCode)
 *   'EMAIL_EXISTS'               — registration conflict
 *   'NOT_FOUND'                  — resource not found (404); student action caused this
 */
export const SYSTEM_ERROR_CODES: ReadonlySet<string> = new Set([
  // 5xx / server-side failures
  'SERVER_ERROR',       // 500 / 503 (mapStatusToCode) — backend or LLM provider crashed
  'PROVIDER_TIMEOUT',   // LLM provider timed out after retries (backend surfaces this code)
  'CONFIG_ERROR',       // Backend misconfiguration (missing env vars, bad tier config, etc.)

  // Network-level failures
  'NETWORK_ERROR',      // No response at all (axios interceptor, no-response path)

  // Rate limiting — genuinely a system/infra issue, not a user action
  'RATE_LIMITED',       // 429 (mapStatusToCode) — infra-level throttle

  // Auth failures that survived silent refresh (AC-3 failure branch)
  // Note: a SUCCESSFUL silent refresh never surfaces UNAUTHORIZED to this gate.
  // FORBIDDEN is included because a 403 on a session the student should own indicates
  // a permission inconsistency (e.g. session ownership bug) rather than a deliberate
  // student action.
  'UNAUTHORIZED',       // 401 after refresh failure — genuine auth system failure
  'FORBIDDEN',          // 403 ownership inconsistency — not caused by student action
]);

/**
 * Returns true only if the given error code is a genuine system error that
 * should show the "Report a problem" affordance.
 *
 * isSystemError(undefined) and isSystemError(null) always return false —
 * the button is never shown when there is no current error (AC-2 default).
 *
 * @param code - The AuthError.code from the current API error, or undefined/null
 * @returns Whether the error code is in the system-error allow-list
 *
 * Note for callers: when a serverless session returns is_fallback=true after a
 * backend retry, the accompanying error code (SERVER_ERROR or PROVIDER_TIMEOUT)
 * should be surfaced as currentErrorCode — this module only classifies the code;
 * the calling component (SessionChatPage) is responsible for extracting it.
 *
 * Implements US-016 AC-1/AC-2/AC-3
 */
export function isSystemError(code?: string | null): boolean {
  // Guard: undefined/null means no error is present → button hidden (AC-2 default)
  if (code == null) {
    return false;
  }
  return SYSTEM_ERROR_CODES.has(code);
}
